/* ── The Guided Tour button and modal, as one state machine ───────────
   Written on 2026-10-10, after a day in which the same area broke four
   times in four different ways: a freshly created room announced "This
   tour has ended"; the button stayed gold while the modal refused; a
   refusal survived the tour it referred to; and a tour on THIS project
   was invisible to the check meant to find it.

   None of those were hard bugs. They were all the same bug: the truth
   lived in four places at once — a `status` string, a claim in React
   state, a second claim in localStorage, a `blockingRoom`, and a ref to
   referee between them — and every fix moved one of them and broke
   another. Precedence was implicit, so it was never written down and
   therefore never consistent.

   So the question "what is going on with this project's tour?" has ONE
   answer with ONE name, and the UI reads it rather than recomputing it
   from parts.

   ── THE PHASES ──
     idle        no room of this host's is open. The button invites.
     creating    a create request is in flight.
     ready       THIS modal created a room; links are showing, nobody has
                 entered yet.
     live_here   a room for THIS project is open — rejoin it.
     blocked     a room of this host's is open for ANOTHER project. The
                 backend caps one per host, so nothing can start here.
     ended       the room this modal created has since ended.
     error       a create attempt failed for a reason worth showing.

   ── WHY THE SERVER IS THE AUTHORITY ──
   The host credential in localStorage answers only for the browser that
   made the room: a tour started on a laptop is invisible to the same
   person on their phone. `SERVER_SAYS` is therefore the event that wins,
   and the reducer's job is to decide what each answer means in context —
   "no room is open" means `ended` to a modal showing join links, and
   `idle` to a page that was never in a tour.

   Pure and synchronous on purpose: every sequence below is a unit test
   rather than something to be rediscovered by clicking. */

export type GuidedTourPhase =
  | "idle"
  | "creating"
  | "ready"
  | "live_here"
  | "blocked"
  | "ended"
  | "error";

export interface GuidedTourRoom {
  room_id: string;
  join_code: string;
  project_id: string;
  project_name: string;
}

export interface GuidedTourState {
  phase: GuidedTourPhase;
  /** The open room, wherever it is. Null only when nothing is open. */
  room: GuidedTourRoom | null;
  /** The open room when it is NOT this project's — what to point the host at. */
  blocking: GuidedTourRoom | null;
  error: string;
}

export const initialGuidedTourState: GuidedTourState = {
  phase: "idle",
  room: null,
  blocking: null,
  error: "",
};

export type GuidedTourEvent =
  /* The host pressed create. */
  | { type: "CREATE_STARTED" }
  /* A room exists now, made by this modal. */
  | { type: "CREATED"; room: GuidedTourRoom }
  /* The backend refused: one room per host. `room` when it told us which. */
  | { type: "CONFLICT"; room?: GuidedTourRoom | null; message: string }
  /* Any other create failure. */
  | { type: "FAILED"; message: string }
  /* The authority: what the server says is open for this host right now.
     `room` null means nothing is open. `isThisProject` is the caller's
     comparison, kept out of the reducer so projectId never has to be
     threaded through it. */
  | { type: "SERVER_SAYS"; room: GuidedTourRoom | null; isThisProject: boolean }
  /* The host opened the room tab. */
  | { type: "ENTERED" }
  /* Start again after an ended tour, or close and reopen the modal. */
  | { type: "RESET" };

export const guidedTourReducer = (
  state: GuidedTourState,
  event: GuidedTourEvent,
): GuidedTourState => {
  switch (event.type) {
    case "CREATE_STARTED":
      return { ...state, phase: "creating", error: "" };

    case "CREATED":
      /* `ready` outranks whatever the server last said. The create
         response is newer than any poll, and a host watching their join
         links appear must not be told a tour has ended. */
      return { phase: "ready", room: event.room, blocking: null, error: "" };

    case "CONFLICT":
      /* A refusal names the room in the way when the backend tells us
         which — it always does for the per-host cap, and the client used
         to discard it. */
      return {
        phase: "blocked",
        room: event.room || state.room,
        blocking: event.room || state.blocking,
        error: event.message,
      };

    case "FAILED":
      return { ...state, phase: "error", error: event.message };

    case "SERVER_SAYS": {
      /* A create is in flight. Whatever the server last knew predates it —
         including "nothing is open", which is simply the state of the
         world a moment before the room existed. Letting that through put
         the modal back to idle underneath a request still running, and
         the room then appeared out of nowhere. */
      if (state.phase === "creating") {
        return event.room ? { ...state, room: event.room } : state;
      }

      if (!event.room) {
        /* Nothing is open. What that MEANS depends on where we were: a
           modal showing join links has just watched its tour end; a page
           that was never in one is simply free. */
        const endedHere = state.phase === "ready" || state.phase === "live_here";
        return {
          phase: endedHere ? "ended" : "idle",
          room: null,
          blocking: null,
          error: "",
        };
      }

      /* A room is open. If this modal is mid-create or showing the links
         for that same room, stay where we are — the server is only
         confirming what we already did. */
      const sameRoom = state.room?.room_id === event.room.room_id;
      if (state.phase === "ready" && sameRoom) {
        return { ...state, room: event.room, blocking: null, error: "" };
      }

      if (event.isThisProject) {
        return { phase: "live_here", room: event.room, blocking: null, error: "" };
      }
      return {
        phase: "blocked",
        room: event.room,
        blocking: event.room,
        error: "",
      };
    }

    case "ENTERED":
      /* Entering does not change what is true, only what the host is
         looking at — but a room nobody could have entered is no longer
         merely `ready`. */
      return state.phase === "ready" ? { ...state, phase: "live_here" } : state;

    case "RESET":
      return { ...initialGuidedTourState };

    default:
      return state;
  }
};

/* ── What the UI asks it ─────────────────────────────────────────────
   Named questions rather than phase comparisons scattered through JSX,
   so a new phase cannot silently change the meaning of a button. */

/** Nothing of this host's is open: the button invites a new tour. */
export const canStartTour = (s: GuidedTourState): boolean =>
  s.phase === "idle" || s.phase === "ended" || s.phase === "error";

/** A tour for THIS project is open — the button rejoins it. */
export const isLiveHere = (s: GuidedTourState): boolean =>
  s.phase === "live_here" || s.phase === "ready";

/** A tour of this host's is open on a DIFFERENT project. */
export const isBlockedElsewhere = (s: GuidedTourState): boolean =>
  s.phase === "blocked";

/** The room the button should open, if any. */
export const roomToOpen = (s: GuidedTourState): GuidedTourRoom | null =>
  isLiveHere(s) || isBlockedElsewhere(s) ? s.room : null;

/** The room this modal made has ended — show that, not a dead link. */
export const hasEnded = (s: GuidedTourState): boolean => s.phase === "ended";
